import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Collapse } from '@mantine/core';
import { useId, useState } from 'react';
import { summarizeTodos } from './flora-todo-bar.helpers';
import { TodoStatus } from './flora-todo-bar.type';
import type { FloraTodoBarProps } from './flora-todo-bar.type';

const STATUS_ICON: Record<TodoStatus, string> = {
  [TodoStatus.COMPLETED]: 'material-symbols:check-circle-rounded',
  [TodoStatus.IN_PROGRESS]: 'material-symbols:progress-activity',
  [TodoStatus.PENDING]: 'material-symbols:radio-button-unchecked',
};

/** Human-readable status suffix, announced to assistive tech per todo item. */
const STATUS_LABEL: Record<TodoStatus, string> = {
  [TodoStatus.COMPLETED]: 'completed',
  [TodoStatus.IN_PROGRESS]: 'in progress',
  [TodoStatus.PENDING]: 'pending',
};

/**
 * Collapsible todo bar docked above the message bar, visualizing Flora's
 * `write_todos` plan for the current turn. Collapsed by default: the header
 * shows completed/total progress and the active task; expanding reveals every
 * todo with a status icon and a strike-through on completed items.
 *
 * @param {FloraTodoBarProps} props - The current todos and live-turn flag.
 * @returns {JSX.Element | null} The bar, or null when there are no todos.
 */
function FloraTodoBar({ todos, isLive = false }: FloraTodoBarProps) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  if (todos.length === 0) {
    return null;
  }

  const { total, completed, activeLabel } = summarizeTodos(todos);
  const allDone = completed === total;
  const summaryLabel = `Tasks, ${completed} of ${total} complete. ${expanded ? 'Collapse' : 'Expand'} task list.`;

  return (
    <Wrapper>
      <Bar>
        <Header
          type="button"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-label={summaryLabel}
          onClick={() => setExpanded((current) => !current)}
        >
          <HeaderIcon
            icon={allDone ? 'material-symbols:task-alt-rounded' : 'material-symbols:checklist-rounded'}
            width={16}
            height={16}
            $done={allDone}
          />
          <HeaderTitle>Tasks</HeaderTitle>
          <Progress $done={allDone}>
            {completed}/{total}
          </Progress>
          {!expanded && activeLabel && !allDone && <ActiveLabel>{activeLabel}</ActiveLabel>}
          {isLive && !allDone && <LivePulse aria-hidden="true" />}
          <Chevron
            icon="material-symbols:keyboard-arrow-up-rounded"
            width={18}
            height={18}
            $open={expanded}
            aria-hidden="true"
          />
        </Header>
        <Collapse expanded={expanded}>
          <List id={listId} aria-live="polite">
            {todos.map((todo, index) => {
              const spinning = isLive && todo.status === TodoStatus.IN_PROGRESS;
              return (
                <Item key={`${index}-${todo.content}`} data-status={todo.status}>
                  <StatusIcon
                    icon={STATUS_ICON[todo.status]}
                    width={16}
                    height={16}
                    $status={todo.status}
                    $spinning={spinning}
                    aria-hidden="true"
                  />
                  <ItemText $status={todo.status}>{todo.content}</ItemText>
                  <VisuallyHidden>({STATUS_LABEL[todo.status]})</VisuallyHidden>
                </Item>
              );
            })}
          </List>
        </Collapse>
      </Bar>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
`;

const Bar = styled.div`
  /* Narrower than the input container so the flat bottom reads as a tab
     plugged into the message input below. */
  width: calc(100% - 40px);
  /* A shade darker than the input (--sky-lightest) so the bar stands out
     against it rather than blending in. */
  background: var(--sky-lighter);
  border: 1px solid var(--sky-light);
  /* Flat, borderless bottom: merges into the message input directly below
     rather than closing off as its own rounded card. */
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  position: relative;
  z-index: 1;
  overflow: hidden;
  animation: flora-todo-bar-in 0.25s ease both;

  @keyframes flora-todo-bar-in {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Header = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: var(--flora-hover-subtle);
  }
`;

const HeaderIcon = styled(Icon)<{ $done: boolean }>`
  flex-shrink: 0;
  color: ${({ $done }) => ($done ? 'var(--primary-base)' : 'var(--ink-light)')};
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-darkest);
  flex-shrink: 0;
`;

const Progress = styled.span<{ $done: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $done }) => ($done ? 'var(--primary-base)' : 'var(--ink-lighter)')};
  flex-shrink: 0;
`;

const ActiveLabel = styled.span`
  font-size: 12.5px;
  color: var(--ink-light);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
  min-width: 0;
`;

const LivePulse = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--secondary-base);
  flex-shrink: 0;
  margin-left: auto;
  animation: flora-todo-pulse 1.2s ease-in-out infinite;

  @keyframes flora-todo-pulse {
    0%,
    100% {
      opacity: 0.35;
    }
    50% {
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const Chevron = styled(Icon)<{ $open: boolean }>`
  flex-shrink: 0;
  margin-left: 4px;
  color: var(--ink-lighter);
  transform: rotate(${({ $open }) => ($open ? '0deg' : '180deg')});
  transition: transform 0.15s;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 2px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 220px;
  overflow-y: auto;
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 2px 0;
`;

const StatusIcon = styled(Icon)<{ $status: TodoStatus; $spinning: boolean }>`
  flex-shrink: 0;
  margin-top: 1px;
  color: ${({ $status }) =>
    $status === TodoStatus.COMPLETED
      ? 'var(--primary-base)'
      : $status === TodoStatus.IN_PROGRESS
        ? 'var(--secondary-base)'
        : 'var(--ink-lighter)'};
  animation: ${({ $spinning }) => ($spinning ? 'flora-todo-spin 0.9s linear infinite' : 'none')};

  @keyframes flora-todo-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ItemText = styled.span<{ $status: TodoStatus }>`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ $status }) => ($status === TodoStatus.COMPLETED ? 'var(--ink-lighter)' : 'var(--ink-dark)')};
  text-decoration: ${({ $status }) => ($status === TodoStatus.COMPLETED ? 'line-through' : 'none')};
`;

/* Status text exposed to assistive tech only; hidden visually since the status
   is conveyed to sighted users by the icon, color and strike-through. */
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export { FloraTodoBar };
